import React, { useState } from "react";
import { Card, Collapse, Descriptions, Tag, Timeline, Table, Space, Alert, Divider, Typography } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  MailOutlined,
  ShoppingCartOutlined,
  DollarOutlined,
  FileTextOutlined,
  SplitCellsOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const { Panel } = Collapse;
const { Title, Paragraph, Text } = Typography;

const Documentation = () => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState([]);

  const statusColors = {
    draft: "default",
    pending_confirmation: "gold",
    pending_approval: "volcano",
    pending_supplier_review: "gold",
    payment_pending: "orange",
    deposit_paid: "cyan",
    paid: "lime",
    ready_for_production: "geekblue",
    in_production: "blue",
    shipped: "orange",
    received: "cyan",
    under_review: "purple",
    completed: "green",
    cancelled: "red",
    split: "magenta",
  };

  const statusFlowData = [
    {
      status: "Draft",
      color: "default",
      description: "Order wordt samengesteld in het CMS",
      actions: "Producten toevoegen, leverancier selecteren, details invullen",
      nextStatus: "Pending Confirmation",
      trigger: "Bombyxx klikt op 'Set to Ordered'",
    },
    {
      status: "Pending Confirmation",
      color: "gold",
      description: "Wacht op bevestiging van leverancier",
      actions: "Leverancier vult CBM, ETD, verpakkingen en dimensies in",
      nextStatus: "Pending Approval",
      trigger: "Leverancier klikt op 'Confirm Order'",
    },
    {
      status: "Pending Approval",
      color: "volcano",
      description: "Bombyxx moet leverancier wijzigingen goedkeuren",
      actions: "Bombyxx controleert aantallen, prijzen, CBM en dimensies",
      nextStatus: "Ready for Production / Pending Supplier Review",
      trigger: "Bombyxx klikt op 'Approve Changes' of 'Save and Send to Supplier'",
    },
    {
      status: "Pending Supplier Review",
      color: "gold",
      description: "Leverancier moet Bombyxx aanpassingen reviewen",
      actions: "Leverancier controleert aangepaste aantallen",
      nextStatus: "Pending Approval",
      trigger: "Leverancier past aan en klikt op 'Confirm Order'",
    },
    {
      status: "Ready for Production",
      color: "geekblue",
      description: "Order is goedgekeurd, wacht op betaling",
      actions: "Bombyxx verwerkt betaling in CMS",
      nextStatus: "In Production",
      trigger: "Betaling status wordt 'Paid'",
    },
    {
      status: "In Production",
      color: "blue",
      description: "Leverancier is bezig met productie",
      actions: "Leverancier kan invoice uploaden, tracking code toevoegen",
      nextStatus: "Shipped",
      trigger: "Leverancier klikt op 'Mark as Shipped' of voegt tracking code toe",
    },
    {
      status: "Shipped",
      color: "orange",
      description: "Order is verzonden door leverancier",
      actions: "Wachten op levering bij Bombyxx",
      nextStatus: "Received",
      trigger: "Bombyxx klikt op 'Set to Received'",
    },
    {
      status: "Received",
      color: "cyan",
      description: "Order is ontvangen bij Bombyxx",
      actions: "Bombyxx telt producten en controleert kwaliteit",
      nextStatus: "Under Review",
      trigger: "Bombyxx klikt op 'Set to Under Review'",
    },
    {
      status: "Under Review",
      color: "purple",
      description: "Bombyxx reviewt ontvangen producten",
      actions: "Discrepanties registreren, kwaliteit controleren",
      nextStatus: "Completed",
      trigger: "Bombyxx klikt op 'Complete Order'",
    },
    {
      status: "Completed",
      color: "green",
      description: "Order is volledig afgerond",
      actions: "Geen acties meer mogelijk",
      nextStatus: "-",
      trigger: "-",
    },
  ];

  const emailNotifications = [
    {
      trigger: "Status: Draft → Pending Confirmation",
      recipient: "Leverancier",
      subject: "Action Required: Confirm Purchase Order",
      content: "Nieuwe order ontvangen, bevestig de order in het Supplier Portal",
    },
    {
      trigger: "Status: Pending Confirmation → Pending Approval",
      recipient: "Bombyxx",
      subject: "Actie vereist: Leverancier wijzigingen",
      content: "Leverancier heeft order bevestigd met CBM/ETD, controleer en keur goed",
    },
    {
      trigger: "Status: Pending Approval → Pending Supplier Review",
      recipient: "Leverancier",
      subject: "Bombyxx has made adjustments",
      content: "Bombyxx heeft aantallen aangepast (CBM optimalisatie), review de wijzigingen",
    },
    {
      trigger: "Status: Ready for Production → In Production",
      recipient: "Bombyxx",
      subject: "Productie gestart",
      content: "Leverancier heeft productie gestart",
    },
    {
      trigger: "Betaling status: Pending → Paid",
      recipient: "Leverancier",
      subject: "Payment Received",
      content: "Betaling is ontvangen, productie kan starten",
    },
    {
      trigger: "Status: In Production → Shipped",
      recipient: "Bombyxx",
      subject: "Order verzonden",
      content: "Leverancier heeft order verzonden, tracking info beschikbaar",
    },
    {
      trigger: "Status: Shipped → Received",
      recipient: "Leverancier",
      subject: "Order Delivered to Bombyxx",
      content: "Order is ontvangen bij Bombyxx, telling wordt uitgevoerd",
    },
    {
      trigger: "Status: Under Review → Completed",
      recipient: "Leverancier",
      subject: "Order Completed",
      content: "Order is volledig afgerond",
    },
    {
      trigger: "Additional Costs toegevoegd",
      recipient: "Bombyxx",
      subject: "Extra kosten toegevoegd",
      content: "Leverancier heeft extra kosten toegevoegd aan de invoice",
    },
  ];

  const invoiceProcess = [
    {
      step: "1. Proforma Invoice (Optioneel)",
      description: "Leverancier kan een proforma invoice uploaden voordat productie start",
      fields: ["Proforma Invoice Number", "Proforma Invoice Date", "Proforma PDF"],
      timing: "Tijdens In Production status",
      location: "Supplier Portal - Invoice Modal - Proforma tab",
    },
    {
      step: "2. Final Invoice",
      description: "Leverancier uploadt de definitieve factuur met alle details",
      fields: [
        "Invoice Number",
        "Invoice Date",
        "Invoice Lines (automatisch gegenereerd op basis van producten)",
        "Additional Costs (optioneel: mold costs, photoshoot, etc.)",
        "Remarks",
        "Invoice PDF",
      ],
      timing: "Tijdens of na In Production status",
      location: "Supplier Portal - Invoice Modal - Final Invoice tab",
    },
    {
      step: "3. Additional Costs",
      description: "Extra kosten kunnen worden toegevoegd aan de final invoice",
      fields: ["Description", "Amount", "Currency", "Quantity (optioneel)", "Unit Price (optioneel)", "Remarks"],
      timing: "Bij het uploaden of updaten van de final invoice",
      notification: "Bombyxx ontvangt automatisch een email notificatie",
    },
    {
      step: "4. Invoice Synchronisatie",
      description: "Invoice wordt automatisch gesynchroniseerd naar Exact Online",
      fields: ["Purchase Invoice ID wordt opgeslagen in Firestore"],
      timing: "Automatisch via Cloud Function",
      location: "Exact Online - Purchase Invoices",
    },
  ];

  const splitOrderProcess = [
    {
      step: "1. Initiëren Split",
      description: "Leverancier opent de Split Order modal",
      location: "Supplier Portal - Order Detail - 'Split Order' button",
      requirement: "Order moet in 'In Production' status zijn",
    },
    {
      step: "2. Verdelen Quantities",
      description: "Leverancier verdeelt product quantities over twee groepen",
      validation: "Totaal moet exact gelijk zijn aan originele quantity per product",
      example: "Product A: 100 stuks → Split in 20 (Order A) + 80 (Order B)",
    },
    {
      step: "3. ETD en CBM Invullen",
      description: "Beide nieuwe orders krijgen eigen ETD en CBM",
      fields: ["ETD Now (Order A)", "ETD Later (Order B)", "CBM Now (Order A)", "CBM Later (Order B)"],
      validation: "Alle velden zijn verplicht",
    },
    {
      step: "4. Invoice Aanpassing",
      description: "Invoice wordt automatisch proportioneel aangepast",
      proforma: "Blijft ongewijzigd bij beide orders",
      finalInvoice: "Line items worden proportioneel aangepast op basis van nieuwe quantities",
      additionalCosts: "Worden volledig toegewezen aan Order A (eerste order)",
      pdfRemoval: "Final invoice PDF wordt verwijderd (moet opnieuw geüpload worden)",
    },
    {
      step: "5. Nieuwe Orders",
      description: "Twee nieuwe child orders worden aangemaakt",
      orderA: "Krijgt suffix '-A', bevat 'Now' producten, ETD en CBM",
      orderB: "Krijgt suffix '-B', bevat 'Later' producten, ETD en CBM",
      originalOrder: "Status wordt 'Split', blijft zichtbaar met link naar child orders",
    },
  ];

  const paymentProcess = [
    {
      status: "Payment Pending",
      description: "Order wacht op betaling",
      action: "Bombyxx voegt betaling toe via 'Add Payment' button",
      fields: ["Amount", "Payment Date", "Notes (optioneel)"],
    },
    {
      status: "Deposit Paid",
      description: "Aanbetaling is gedaan",
      calculation: "Totaal betaald < Invoice Total Amount",
      action: "Bombyxx kan aanvullende betalingen toevoegen",
    },
    {
      status: "Paid",
      description: "Volledig betaald",
      calculation: "Totaal betaald ≥ Invoice Total Amount",
      trigger: "Status wordt automatisch 'Ready for Production'",
    },
  ];

  const productMasterDataUpdate = [
    {
      trigger: "Order Creation",
      description: "Bij het aanmaken van een nieuwe purchase order",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als waarden verschillen van huidige product master data",
    },
    {
      trigger: "Approve Supplier Changes",
      description: "Wanneer Bombyxx leverancier wijzigingen goedkeurt",
      updates: ["Purchase Price", "Inner Packaging Quantity", "Master Packaging Quantity"],
      condition: "Als adjusted values verschillen van originele values",
      location: "CMS - Order Detail - 'Approve Changes' button",
    },
  ];

  const productDraftProcess = [
    {
      step: "1. Product Draft Aanmaken",
      description: "Leverancier maakt een nieuw product draft aan in het Supplier Portal",
      location: "Supplier Portal - Order Detail - Product toevoegen",
      fields: ["Product Name", "Product Code", "Barcode (optioneel)"],
      status: "Needs Supplier Input",
    },
    {
      step: "2. Product Details Invullen",
      description: "Leverancier vult alle product details in",
      fields: [
        "Purchase Price",
        "Inner Packaging Quantity",
        "Master Packaging Quantity",
        "Length (optioneel)",
        "Width (optioneel)",
        "Height (optioneel)",
        "Diameter (optioneel)",
        "Color (optioneel)",
        "Product Images (optioneel)",
      ],
      action: "Leverancier klikt op 'Submit for Review'",
      status: "Submitted",
    },
    {
      step: "3. Bombyxx Review",
      description: "Bombyxx controleert de product details in het CMS",
      location: "CMS - Product Drafts pagina",
      options: ["Approve: Product wordt goedgekeurd", "Reject: Wijzigingen vereist met feedback"],
      status: "Submitted → Approved / Changes Requested",
    },
    {
      step: "4. Changes Requested (optioneel)",
      description: "Als Bombyxx wijzigingen vraagt, moet leverancier aanpassen",
      action: "Leverancier past product details aan en submit opnieuw",
      status: "Changes Requested → Submitted",
    },
    {
      step: "5. Product Publiceren",
      description: "Na approval kan Bombyxx het product publiceren naar de catalog",
      action: "Bombyxx klikt op 'Publish' in Product Drafts detail modal",
      result: "Nieuw product wordt aangemaakt in products collection",
      status: "Approved → Published",
    },
    {
      step: "6. Purchase Order Update",
      description: "Purchase order wordt automatisch geüpdatet met published product ID",
      result: "Product draft wordt gekoppeld aan het nieuwe product in de catalog",
    },
  ];

  const productDraftStatuses = [
    {
      status: "Needs Supplier Input",
      color: "default",
      description: "Product draft is aangemaakt maar nog niet compleet",
      action: "Leverancier moet details invullen",
    },
    {
      status: "Submitted",
      color: "processing",
      description: "Leverancier heeft product details ingediend",
      action: "Bombyxx moet reviewen en goedkeuren/afwijzen",
    },
    {
      status: "Changes Requested",
      color: "warning",
      description: "Bombyxx heeft wijzigingen gevraagd",
      action: "Leverancier moet aanpassingen maken",
    },
    {
      status: "Approved",
      color: "success",
      description: "Bombyxx heeft product goedgekeurd",
      action: "Bombyxx kan product publiceren naar catalog",
    },
    {
      status: "Published",
      color: "green",
      description: "Product is gepubliceerd naar de catalog",
      action: "Geen acties meer mogelijk",
    },
  ];

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => <Tag color={record.color}>{text}</Tag>,
    },
    {
      title: "Beschrijving",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Acties",
      dataIndex: "actions",
      key: "actions",
    },
    {
      title: "Volgende Status",
      dataIndex: "nextStatus",
      key: "nextStatus",
    },
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
    },
  ];

  const emailColumns = [
    {
      title: "Trigger",
      dataIndex: "trigger",
      key: "trigger",
    },
    {
      title: "Ontvanger",
      dataIndex: "recipient",
      key: "recipient",
      render: (text) => <Tag color={text === "Bombyxx" ? "blue" : "green"}>{text}</Tag>,
    },
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
    },
    {
      title: "Inhoud",
      dataIndex: "content",
      key: "content",
    },
  ];

  return (
    <Card>
      <Title level={2}>
        <FileTextOutlined /> Inkoopproces documentatie
      </Title>
      <Paragraph>
        Deze documentatie beschrijft het volledige inkoopproces van Bombyxx, inclusief alle statussen, workflows, email
        notificaties en speciale processen zoals facturatie en order splitting.
      </Paragraph>

      <Collapse activeKey={activeKey} onChange={setActiveKey} style={{ marginTop: 24 }}>
        {/* Status Flow */}
        <Panel
          header={
            <Space>
              <ShoppingCartOutlined />
              <strong>1. Purchase Order Status Flow</strong>
            </Space>
          }
          key="1"
        >
          <Alert
            message="Complete Status Flow"
            description="Dit is de normale flow van een purchase order van begin tot eind. Sommige statussen kunnen worden overgeslagen afhankelijk van het proces."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={statusFlowData}
            columns={columns}
            pagination={false}
            rowKey="status"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Visuele Timeline</Title>
          <Timeline mode="left">
            <Timeline.Item color="gray" dot={<ClockCircleOutlined />}>
              <strong>Draft</strong>
              <br />
              Order wordt samengesteld in CMS
            </Timeline.Item>
            <Timeline.Item color="gold" dot={<ExclamationCircleOutlined />}>
              <strong>Pending Confirmation</strong>
              <br />
              Leverancier vult CBM, ETD en product details in
            </Timeline.Item>
            <Timeline.Item color="volcano" dot={<ExclamationCircleOutlined />}>
              <strong>Pending Approval</strong>
              <br />
              Bombyxx controleert en keurt goed
            </Timeline.Item>
            <Timeline.Item color="geekblue" dot={<DollarOutlined />}>
              <strong>Ready for Production</strong>
              <br />
              Wacht op betaling
            </Timeline.Item>
            <Timeline.Item color="blue" dot={<ClockCircleOutlined />}>
              <strong>In Production</strong>
              <br />
              Leverancier produceert
            </Timeline.Item>
            <Timeline.Item color="orange" dot={<InfoCircleOutlined />}>
              <strong>Shipped</strong>
              <br />
              Order is verzonden
            </Timeline.Item>
            <Timeline.Item color="cyan" dot={<CheckCircleOutlined />}>
              <strong>Received</strong>
              <br />
              Order is ontvangen bij Bombyxx
            </Timeline.Item>
            <Timeline.Item color="purple" dot={<ClockCircleOutlined />}>
              <strong>Under Review</strong>
              <br />
              Bombyxx controleert kwaliteit
            </Timeline.Item>
            <Timeline.Item color="green" dot={<CheckCircleOutlined />}>
              <strong>Completed</strong>
              <br />
              Order is afgerond
            </Timeline.Item>
          </Timeline>

          <Divider />

          <Title level={4}>Alternatieve Flows</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Pending Approval → Pending Supplier Review">
              Bombyxx past aantallen aan (CBM optimalisatie) en stuurt terug naar leverancier
            </Descriptions.Item>
            <Descriptions.Item label="Pending Supplier Review → Pending Approval">
              Leverancier past aan en stuurt terug naar Bombyxx voor goedkeuring
            </Descriptions.Item>
            <Descriptions.Item label="In Production → Split">
              Leverancier splitst order in twee aparte orders met verschillende ETD's
            </Descriptions.Item>
            <Descriptions.Item label="Elke status → Cancelled">
              Order kan op elk moment worden geannuleerd (behalve Completed)
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Email Notifications */}
        <Panel
          header={
            <Space>
              <MailOutlined />
              <strong>2. Email Notificaties</strong>
            </Space>
          }
          key="2"
        >
          <Alert
            message="Automatische Email Notificaties"
            description="Alle emails worden automatisch verstuurd via Cloud Functions wanneer bepaalde triggers plaatsvinden. Emails worden in een queue geplaatst en binnen 1 minuut verzonden."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Table
            dataSource={emailNotifications}
            columns={emailColumns}
            pagination={false}
            rowKey="trigger"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Email Queue Systeem</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Collection">emailQueue (Firestore)</Descriptions.Item>
            <Descriptions.Item label="Processing">Cloud Function checkt elke minuut op nieuwe emails</Descriptions.Item>
            <Descriptions.Item label="Status">scheduled → sent / failed</Descriptions.Item>
            <Descriptions.Item label="Retry Logic">Max 3 retries bij failures, 1 uur tussen retries</Descriptions.Item>
            <Descriptions.Item label="Email Service">Java Spring Boot API (MarketingService)</Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Invoice Process */}
        <Panel
          header={
            <Space>
              <FileTextOutlined />
              <strong>3. Facturatie Proces</strong>
            </Space>
          }
          key="3"
        >
          <Alert
            message="Invoice Workflow"
            description="Leveranciers kunnen zowel een proforma invoice als een final invoice uploaden. Additional costs kunnen worden toegevoegd voor extra kosten zoals mold costs of photoshoot kosten."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {invoiceProcess.map((item, index) => (
              <Timeline.Item key={index} color="blue">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.timing && (
                  <Paragraph>
                    <Text strong>Timing:</Text> {item.timing}
                  </Paragraph>
                )}
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.notification && (
                  <Alert message={item.notification} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Invoice Line Items</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Automatische Generatie">
              Invoice lines worden automatisch gegenereerd op basis van order producten
            </Descriptions.Item>
            <Descriptions.Item label="Velden per Line">
              Product ID, Product Name, Color, Quantity, Unit Price, Discount %, Total Price
            </Descriptions.Item>
            <Descriptions.Item label="Berekening">
              Total Price = Quantity × Unit Price × (1 - Discount% / 100)
            </Descriptions.Item>
            <Descriptions.Item label="Bij Order Split">
              Invoice lines worden proportioneel aangepast op basis van nieuwe quantities
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Additional Costs</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Voorbeelden">
              Mold costs, Photoshoot kosten, Sample kosten, Shipping kosten
            </Descriptions.Item>
            <Descriptions.Item label="Velden">
              Description, Amount, Currency, Quantity (optioneel), Unit Price (optioneel), Remarks
            </Descriptions.Item>
            <Descriptions.Item label="Email Notificatie">
              Bombyxx ontvangt automatisch een email wanneer additional costs worden toegevoegd
            </Descriptions.Item>
            <Descriptions.Item label="Bij Order Split">
              Alle additional costs worden toegewezen aan de eerste child order (Order A)
            </Descriptions.Item>
            <Descriptions.Item label="Weergave in CMS">
              Total Price toont breakdown: Producten + Extra Kosten = Totaal
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Invoice Synchronisatie naar Exact Online</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Trigger">
              Automatisch via Cloud Function wanneer invoice wordt toegevoegd/geüpdatet
            </Descriptions.Item>
            <Descriptions.Item label="API Endpoint">POST /api/exact/purchase_order/invoice</Descriptions.Item>
            <Descriptions.Item label="Exact Purchase Invoice">
              Wordt aangemaakt in Exact Online met alle invoice lines en additional costs
            </Descriptions.Item>
            <Descriptions.Item label="Exact ID">
              Purchase Invoice ID wordt opgeslagen in Firestore (exactPurchaseInvoiceId)
            </Descriptions.Item>
            <Descriptions.Item label="Status Indicator">
              CMS toont "✓ Synced to Exact" tag wanneer synchronisatie succesvol is
            </Descriptions.Item>
          </Descriptions>
        </Panel>

        {/* Order Splitting */}
        <Panel
          header={
            <Space>
              <SplitCellsOutlined />
              <strong>4. Order Splitting</strong>
            </Space>
          }
          key="4"
        >
          <Alert
            message="Order Split Functionaliteit"
            description="Leveranciers kunnen een order splitsen in twee aparte orders met verschillende ETD's. Dit is handig wanneer producten op verschillende momenten klaar zijn."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {splitOrderProcess.map((item, index) => (
              <Timeline.Item key={index} color="purple">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.requirement && (
                  <Alert message={item.requirement} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
                {item.validation && (
                  <Paragraph>
                    <Text strong>Validatie:</Text> {item.validation}
                  </Paragraph>
                )}
                {item.example && (
                  <Paragraph>
                    <Text strong>Voorbeeld:</Text> {item.example}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.proforma && (
                  <Paragraph>
                    <Text strong>Proforma Invoice:</Text> {item.proforma}
                  </Paragraph>
                )}
                {item.finalInvoice && (
                  <Paragraph>
                    <Text strong>Final Invoice:</Text> {item.finalInvoice}
                  </Paragraph>
                )}
                {item.additionalCosts && (
                  <Paragraph>
                    <Text strong>Additional Costs:</Text> {item.additionalCosts}
                  </Paragraph>
                )}
                {item.pdfRemoval && (
                  <Alert message={item.pdfRemoval} type="warning" showIcon style={{ marginTop: 8 }} />
                )}
                {item.orderA && (
                  <Paragraph>
                    <Text strong>Order A:</Text> {item.orderA}
                  </Paragraph>
                )}
                {item.orderB && (
                  <Paragraph>
                    <Text strong>Order B:</Text> {item.orderB}
                  </Paragraph>
                )}
                {item.originalOrder && (
                  <Paragraph>
                    <Text strong>Originele Order:</Text> {item.originalOrder}
                  </Paragraph>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Voorbeeld Split Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Originele Order:</Text> IO-20260122-001
            </Paragraph>
            <ul>
              <li>Product A: 100 stuks @ $10 = $1,000</li>
              <li>Product B: 50 stuks @ $20 = $1,000</li>
              <li>Additional Costs: $200 (Mold costs)</li>
              <li>Total: $2,200</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Na Split:</Text>
            </Paragraph>

            <Paragraph>
              <Text strong>Order A (IO-20260122-001-A):</Text>
            </Paragraph>
            <ul>
              <li>Product A: 20 stuks @ $10 = $200</li>
              <li>Product B: 10 stuks @ $20 = $200</li>
              <li>Additional Costs: $200 (Mold costs)</li>
              <li>Total: $600</li>
              <li>ETD: 2026-02-15</li>
              <li>CBM: 2.5 m³</li>
            </ul>

            <Paragraph>
              <Text strong>Order B (IO-20260122-001-B):</Text>
            </Paragraph>
            <ul>
              <li>Product A: 80 stuks @ $10 = $800</li>
              <li>Product B: 40 stuks @ $20 = $800</li>
              <li>Additional Costs: $0</li>
              <li>Total: $1,600</li>
              <li>ETD: 2026-03-15</li>
              <li>CBM: 7.5 m³</li>
            </ul>
          </Card>
        </Panel>

        {/* Payment Process */}
        <Panel
          header={
            <Space>
              <DollarOutlined />
              <strong>5. Betalingsproces</strong>
            </Space>
          }
          key="5"
        >
          <Alert
            message="Payment Status Systeem"
            description="Betalingen worden apart bijgehouden van de order status. Een order kan meerdere betalingen hebben (aanbetaling + restbetaling)."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {paymentProcess.map((item, index) => (
              <Timeline.Item key={index} color="green">
                <Title level={5}>{item.status}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.action && (
                  <Paragraph>
                    <Text strong>Actie:</Text> {item.action}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.calculation && (
                  <Paragraph>
                    <Text strong>Berekening:</Text> {item.calculation}
                  </Paragraph>
                )}
                {item.trigger && <Alert message={item.trigger} type="success" showIcon style={{ marginTop: 8 }} />}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Payment Berekeningen</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Total to Pay">
              Invoice Total Amount (producten + additional costs)
            </Descriptions.Item>
            <Descriptions.Item label="Total Paid">Som van alle payment amounts</Descriptions.Item>
            <Descriptions.Item label="Remaining">Total to Pay - Total Paid</Descriptions.Item>
            <Descriptions.Item label="Payment Status Logic">
              <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                <li>Pending: Total Paid = 0</li>
                <li>Deposit Paid: 0 &lt; Total Paid &lt; Total to Pay</li>
                <li>Paid: Total Paid ≥ Total to Pay</li>
              </ul>
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Automatische Status Wijziging</Title>
          <Alert
            message="Ready for Production → In Production"
            description="Wanneer payment status 'Paid' wordt, verandert de order status automatisch naar 'Ready for Production'. Leverancier kan dan productie starten."
            type="success"
            showIcon
          />
        </Panel>

        {/* Product Master Data */}
        <Panel
          header={
            <Space>
              <InfoCircleOutlined />
              <strong>6. Product Master Data Updates</strong>
            </Space>
          }
          key="6"
        >
          <Alert
            message="Automatische Product Updates"
            description="Wanneer leveranciers prijzen of verpakkingen wijzigen in een purchase order, worden deze automatisch doorgevoerd in de product master data."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {productMasterDataUpdate.map((item, index) => (
              <Timeline.Item key={index} color="cyan">
                <Title level={5}>{item.trigger}</Title>
                <Paragraph>{item.description}</Paragraph>
                <Paragraph>
                  <Text strong>Updates:</Text>
                </Paragraph>
                <ul>
                  {item.updates.map((update, idx) => (
                    <li key={idx}>{update}</li>
                  ))}
                </ul>
                <Paragraph>
                  <Text strong>Conditie:</Text> {item.condition}
                </Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Update Logica</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Vergelijking">
              Order product waarden worden vergeleken met huidige product master data
            </Descriptions.Item>
            <Descriptions.Item label="Update Trigger">
              Alleen als waarden verschillen wordt product master data geüpdatet
            </Descriptions.Item>
            <Descriptions.Item label="Metadata">
              updatedAt (timestamp) en updatedBy (email) worden toegevoegd
            </Descriptions.Item>
            <Descriptions.Item label="Firestore Collection">products</Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Voorbeeld Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Product Master Data (voor order):</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $10.00</li>
              <li>Inner Packaging: 12 stuks</li>
              <li>Master Packaging: 144 stuks</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Leverancier wijzigt in order:</Text>
            </Paragraph>
            <ul>
              <li>Adjusted Price: $9.50</li>
              <li>Adjusted Inner Packaging: 10 stuks</li>
              <li>Adjusted Master Packaging: 120 stuks</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Na Bombyxx Approval:</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $9.50 ✓ Updated</li>
              <li>Inner Packaging: 10 stuks ✓ Updated</li>
              <li>Master Packaging: 120 stuks ✓ Updated</li>
              <li>updatedAt: 1737554400</li>
              <li>updatedBy: sam@digitalkiss.nl</li>
            </ul>
          </Card>
        </Panel>

        {/* Product Drafts */}
        <Panel
          header={
            <Space>
              <PlusOutlined />
              <strong>7. Nieuwe Producten Toevoegen (Product Drafts)</strong>
            </Space>
          }
          key="7"
        >
          <Alert
            message="Product Draft Workflow"
            description="Wanneer een leverancier een nieuw product wil toevoegen dat nog niet in de catalog staat, wordt een Product Draft aangemaakt. Dit doorloopt een goedkeuringsproces voordat het wordt gepubliceerd."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Timeline>
            {productDraftProcess.map((item, index) => (
              <Timeline.Item key={index} color="blue">
                <Title level={5}>{item.step}</Title>
                <Paragraph>{item.description}</Paragraph>
                {item.location && (
                  <Paragraph>
                    <Text strong>Locatie:</Text> {item.location}
                  </Paragraph>
                )}
                {item.fields && (
                  <>
                    <Text strong>Velden:</Text>
                    <ul>
                      {item.fields.map((field, idx) => (
                        <li key={idx}>{field}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.options && (
                  <>
                    <Text strong>Opties:</Text>
                    <ul>
                      {item.options.map((option, idx) => (
                        <li key={idx}>{option}</li>
                      ))}
                    </ul>
                  </>
                )}
                {item.action && (
                  <Paragraph>
                    <Text strong>Actie:</Text> {item.action}
                  </Paragraph>
                )}
                {item.result && (
                  <Paragraph>
                    <Text strong>Resultaat:</Text> {item.result}
                  </Paragraph>
                )}
                {item.status && (
                  <Tag color="blue" style={{ marginTop: 8 }}>
                    {item.status}
                  </Tag>
                )}
              </Timeline.Item>
            ))}
          </Timeline>

          <Divider />

          <Title level={4}>Product Draft Statussen</Title>
          <Table
            dataSource={productDraftStatuses}
            columns={[
              {
                title: "Status",
                dataIndex: "status",
                key: "status",
                render: (text, record) => <Tag color={record.color}>{text}</Tag>,
              },
              {
                title: "Beschrijving",
                dataIndex: "description",
                key: "description",
              },
              {
                title: "Actie",
                dataIndex: "action",
                key: "action",
              },
            ]}
            pagination={false}
            rowKey="status"
            size="small"
            bordered
          />

          <Divider />

          <Title level={4}>Belangrijke Details</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="Firestore Collection">productDrafts</Descriptions.Item>
            <Descriptions.Item label="Revisions">
              Elke status wijziging wordt opgeslagen in een revisions subcollection
            </Descriptions.Item>
            <Descriptions.Item label="Purchase Order Koppeling">
              Product draft wordt gekoppeld aan purchase order via purchaseOrderId en purchaseOrderProductIndex
            </Descriptions.Item>
            <Descriptions.Item label="Published Product">
              Na publicatie wordt publishedProductId opgeslagen in de draft
            </Descriptions.Item>
            <Descriptions.Item label="Existing Product Updates">
              Als een draft een publishedProductId heeft, worden wijzigingen doorgevoerd in het bestaande product
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Voorbeeld Scenario</Title>
          <Card size="small" style={{ backgroundColor: "#f0f5ff" }}>
            <Paragraph>
              <Text strong>Stap 1: Leverancier maakt nieuw product aan</Text>
            </Paragraph>
            <ul>
              <li>Product Name: "Bamboo Water Bottle"</li>
              <li>Product Code: "BWB-001"</li>
              <li>Barcode: "8719327001234"</li>
              <li>Status: Needs Supplier Input</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 2: Leverancier vult details in</Text>
            </Paragraph>
            <ul>
              <li>Purchase Price: $8.50</li>
              <li>Inner Packaging: 12 stuks</li>
              <li>Master Packaging: 144 stuks</li>
              <li>Length: 25 cm</li>
              <li>Diameter: 7 cm</li>
              <li>Color: Natural Bamboo</li>
              <li>Images: 3 product foto's geüpload</li>
              <li>Status: Submitted</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 3: Bombyxx keurt goed</Text>
            </Paragraph>
            <ul>
              <li>Reviewed by: sam@digitalkiss.nl</li>
              <li>Status: Approved</li>
            </ul>

            <Divider />

            <Paragraph>
              <Text strong>Stap 4: Bombyxx publiceert</Text>
            </Paragraph>
            <ul>
              <li>Nieuw product aangemaakt in products collection</li>
              <li>Product ID: abc123xyz</li>
              <li>Purchase order geüpdatet met productId</li>
              <li>Status: Published</li>
            </ul>
          </Card>
        </Panel>

        {/* System Architecture */}
        <Panel
          header={
            <Space>
              <InfoCircleOutlined />
              <strong>8. Systeem Architectuur</strong>
            </Space>
          }
          key="8"
        >
          <Title level={4}>Componenten</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="CMS (Bombyxx)">
              React applicatie voor Bombyxx medewerkers om orders te beheren
            </Descriptions.Item>
            <Descriptions.Item label="Supplier Portal">
              Next.js applicatie voor leveranciers om orders te bekijken en bewerken
            </Descriptions.Item>
            <Descriptions.Item label="API (Java Spring Boot)">
              Backend API voor Exact Online integratie en email verzending
            </Descriptions.Item>
            <Descriptions.Item label="Firestore">
              Database voor alle purchase order data, products, suppliers, etc.
            </Descriptions.Item>
            <Descriptions.Item label="Cloud Functions">
              Serverless functions voor automatische email verzending en status updates
            </Descriptions.Item>
            <Descriptions.Item label="Firebase Storage">
              Opslag voor invoice PDFs, product images, en order attachments
            </Descriptions.Item>
            <Descriptions.Item label="Exact Online">
              ERP systeem voor financiële administratie en voorraad beheer
            </Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Firestore Collections</Title>
          <Descriptions bordered column={1} size="small">
            <Descriptions.Item label="purchaseOrders">
              Alle purchase orders met producten, invoice, payments, log, etc.
            </Descriptions.Item>
            <Descriptions.Item label="products">Product master data (catalog)</Descriptions.Item>
            <Descriptions.Item label="suppliers">Leverancier informatie</Descriptions.Item>
            <Descriptions.Item label="productDrafts">
              Nieuwe producten die nog niet in de catalog staan
            </Descriptions.Item>
            <Descriptions.Item label="emailQueue">Queue voor automatische email verzending</Descriptions.Item>
            <Descriptions.Item label="config">Systeem configuratie (order counter, etc.)</Descriptions.Item>
          </Descriptions>

          <Divider />

          <Title level={4}>Data Flow</Title>
          <Timeline>
            <Timeline.Item color="blue">
              <strong>1. Order Creation (CMS)</strong>
              <br />
              Bombyxx maakt order aan → Firestore purchaseOrders collection
            </Timeline.Item>
            <Timeline.Item color="green">
              <strong>2. Status Change</strong>
              <br />
              Status update → Cloud Function triggered → Email queue entry created
            </Timeline.Item>
            <Timeline.Item color="purple">
              <strong>3. Email Processing</strong>
              <br />
              Cloud Function checkt queue → API MarketingService → Email verzonden
            </Timeline.Item>
            <Timeline.Item color="orange">
              <strong>4. Supplier Action (Portal)</strong>
              <br />
              Leverancier bewerkt order → Firestore update → Status change → Email trigger
            </Timeline.Item>
            <Timeline.Item color="cyan">
              <strong>5. Invoice Sync</strong>
              <br />
              Invoice uploaded → Cloud Function → API Exact endpoint → Exact Online
            </Timeline.Item>
          </Timeline>
        </Panel>

        {/* Troubleshooting */}
        <Panel
          header={
            <Space>
              <ExclamationCircleOutlined />
              <strong>9. Troubleshooting & FAQ</strong>
            </Space>
          }
          key="9"
        >
          <Title level={4}>Veelvoorkomende Problemen</Title>

          <Collapse ghost>
            <Panel header="Email wordt niet verzonden" key="8-1">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Cloud Function is niet actief</li>
                <li>Email queue entry heeft status 'failed'</li>
                <li>API MarketingService is down</li>
                <li>Email adres is incorrect</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Firestore emailQueue collection voor status</li>
                <li>Check Cloud Function logs in Firebase Console</li>
                <li>Check API logs voor errors</li>
                <li>Verifieer email adres in supplier/user data</li>
              </ul>
            </Panel>

            <Panel header="Invoice wordt niet gesynchroniseerd naar Exact" key="8-2">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Exact API credentials zijn verlopen</li>
                <li>Cloud Function is niet getriggered</li>
                <li>Invoice data is incompleet</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Exact API token in API configuratie</li>
                <li>Check Cloud Function logs</li>
                <li>Verifieer invoice heeft alle verplichte velden</li>
                <li>Check exactPurchaseInvoiceId in Firestore</li>
              </ul>
            </Panel>

            <Panel header="Order split werkt niet" key="8-3">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Order is niet in 'In Production' status</li>
                <li>Quantities tellen niet op tot origineel</li>
                <li>ETD of CBM ontbreekt</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Verifieer order status is 'In Production'</li>
                <li>Check dat split quantities exact optellen tot origineel</li>
                <li>Vul alle verplichte velden in (ETD Now, ETD Later, CBM Now, CBM Later)</li>
              </ul>
            </Panel>

            <Panel header="Product master data wordt niet geüpdatet" key="8-4">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>Product heeft geen productId (is een draft product)</li>
                <li>Waarden zijn niet gewijzigd</li>
                <li>updateProductPricesAndPackaging functie wordt niet aangeroepen</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check dat product een productId heeft (geen productDraftId)</li>
                <li>Verifieer dat adjusted values verschillen van originele values</li>
                <li>Check CMS logs voor errors in updateProductPricesAndPackaging</li>
              </ul>
            </Panel>

            <Panel header="Additional costs email wordt niet verzonden" key="8-5">
              <Paragraph>
                <Text strong>Mogelijke oorzaken:</Text>
              </Paragraph>
              <ul>
                <li>additionalCostsNotificationPending flag is niet gezet</li>
                <li>Cloud Function onUpdatePurchaseOrder is niet getriggered</li>
                <li>Email type is niet toegevoegd aan MarketingService</li>
              </ul>
              <Paragraph>
                <Text strong>Oplossing:</Text>
              </Paragraph>
              <ul>
                <li>Check Firestore order voor additionalCostsNotificationPending flag</li>
                <li>Check Cloud Function logs</li>
                <li>Verifieer MarketingService heeft 'purchase_order_additional_costs_added' case</li>
              </ul>
            </Panel>
          </Collapse>

          <Divider />

          <Title level={4}>Belangrijke Notities</Title>
          <Alert
            message="Status vs Payment Status"
            description="Order status en payment status zijn gescheiden. Een order kan 'Ready for Production' zijn maar nog 'Payment Pending' als payment status."
            type="warning"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Invoice Total Price"
            description="Total Price in overzichtstabel toont altijd producten + additional costs. In detail pagina wordt breakdown getoond."
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Order Splitting"
            description="Na split blijft originele order zichtbaar met status 'Split'. Child orders krijgen suffix -A en -B."
            type="info"
            showIcon
            style={{ marginBottom: 8 }}
          />
          <Alert
            message="Email Delays"
            description="Emails worden binnen 1 minuut verzonden na trigger. Check emailQueue collection voor status."
            type="info"
            showIcon
          />
        </Panel>
      </Collapse>
    </Card>
  );
};

export default Documentation;
